import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/** Cache module-level del metadata allergeni (14 EU) — evita 1 fetch per ogni card */
let _metadataCache = null;
let _metadataPromise = null;

const fetchAllergeniMetadata = async (token) => {
  if (_metadataCache) return _metadataCache;
  if (_metadataPromise) return _metadataPromise;
  _metadataPromise = fetch(`${API}/api/allergeni/metadata`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((r) => (r.ok ? r.json() : []))
    .then((arr) => {
      const map = {};
      (arr || []).forEach((a) => { map[a.key] = a; });
      _metadataCache = map;
      return map;
    })
    .catch(() => ({}));
  return _metadataPromise;
};

/**
 * Badge allergeni compatti (icona + label) per ricette / menu / prodotti.
 *
 * Props:
 *   allergeni: List[str] — slug allergeni EU
 *   nonVerificati?: int — quanti ingredienti hanno allergeni non confermati
 *   calcolabile?: bool — false se la ricetta è "light" senza legame ingredienti DB
 *   maxVisible?: int (default 5)
 *   size?: "xs" | "sm" (default "xs")
 */
const AllergeniBadges = ({ allergeni, nonVerificati = 0, calcolabile = true, maxVisible = 5, size = "xs" }) => {
  const { token } = useAuth();
  const [meta, setMeta] = useState(_metadataCache || {});

  useEffect(() => {
    if (!token || _metadataCache) return;
    fetchAllergeniMetadata(token).then(setMeta);
  }, [token]);

  if (!calcolabile) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground italic">
        <AlertTriangle className="h-3 w-3" />
        Allergeni non calcolabili (ricetta semplificata)
      </div>
    );
  }

  if (!allergeni || allergeni.length === 0) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
        <ShieldCheck className="h-3 w-3" />
        Nessun allergene rilevato
      </div>
    );
  }

  const textSize = size === "sm" ? "text-[11px]" : "text-[10px]";
  const visible = allergeni.slice(0, maxVisible);
  const rest = allergeni.length - maxVisible;

  return (
    <div className="flex items-center flex-wrap gap-1" data-testid="allergeni-badges">
      {visible.map((slug) => {
        const m = meta[slug];
        return (
          <span
            key={slug}
            className={`${textSize} px-1.5 py-0.5 rounded-full border font-medium inline-flex items-center gap-0.5 bg-rose-50 border-rose-200 text-rose-800`}
            title={m?.label || slug}
          >
            {m ? <><span>{m.icon}</span><span>{m.label}</span></> : slug}
          </span>
        );
      })}
      {rest > 0 && (
        <span className={`${textSize} text-muted-foreground font-medium`}>+{rest}</span>
      )}
      {nonVerificati > 0 && (
        <span
          className={`${textSize} text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5 font-medium`}
          title="Alcuni ingredienti hanno allergeni non ancora confermati"
        >
          <AlertTriangle className="h-2.5 w-2.5" />
          {nonVerificati} da verificare
        </span>
      )}
    </div>
  );
};

export default AllergeniBadges;
